import React, { useEffect, useState } from 'react';
import { X, Phone, User, Send, CheckCircle } from 'lucide-react';
import { useLeadForm } from './LeadFormContext';
import { trackPixelEvent } from './metaPixel';
// 🔗 Вставьте сюда URL вашего Google Apps Script Web App (см. инструкцию)
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbypXwYrOnLyszoRwdqF_04MDBqJfbu9lKqVd1-xMIgzJbqLNF0pCCWEQkBdG2PwrH39rw/exec';
function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  const d = digits.startsWith('7') ? digits : digits.startsWith('8') ? '7' + digits.slice(1) : digits;
  if (d.length === 0) return '';
  if (d.length <= 1) return '+7';
  if (d.length <= 4) return `+7 (${d.slice(1)}`;
  if (d.length <= 7) return `+7 (${d.slice(1, 4)}) ${d.slice(4)}`;
  if (d.length <= 9) return `+7 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return `+7 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9, 11)}`;
}
function getDigits(formatted: string): string {
  return formatted.replace(/\D/g, '');
}
export function LeadFormModal() {
  const {
    isOpen,
    closeForm
  } = useLeadForm();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<{
    name?: string;
    phone?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) {
      // Reset form when closed
      setTimeout(() => {
        setName('');
        setPhone('');
        setErrors({});
        setIsSubmitting(false);
        setIsSuccess(false);
        setSubmitError('');
      }, 300);
    }
  }, [isOpen]);
  function validate(): boolean {
    const newErrors: {
      name?: string;
      phone?: string;
    } = {};
    if (!name.trim()) {
      newErrors.name = 'Введите ваше имя';
    } else if (name.trim().length < 2) {
      newErrors.name = 'Имя слишком короткое';
    }
    const digits = getDigits(phone);
    if (!digits) {
      newErrors.phone = 'Введите номер телефона';
    } else if (digits.length !== 11) {
      newErrors.phone = 'Введите полный номер: +7 (XXX) XXX-XX-XX';
    } else if (!digits.startsWith('7')) {
      newErrors.phone = 'Номер должен начинаться с +7';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }
  function handlePhoneChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    const digits = raw.replace(/\D/g, '');
    if (digits.length <= 11) {
      setPhone(formatPhone(raw));
    }
    if (errors.phone) {
      setErrors(prev => ({
        ...prev,
        phone: undefined
      }));
    }
  }
  function handleNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    setName(e.target.value);
    if (errors.name) {
      setErrors(prev => ({
        ...prev,
        name: undefined
      }));
    }
  }
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitError('');
    const lead = {
      name: name.trim(),
      phone,
      page: typeof window !== 'undefined' ? window.location.href : '',
      timestamp: new Date().toISOString()
    };
    try {
      // Google Apps Script Web App doesn't return CORS headers,
      // so we use no-cors mode and treat completion as success.
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8'
        },
        body: JSON.stringify(lead)
      });
      // Meta Pixel: track lead conversion
      trackPixelEvent('Lead', {
        content_name: 'Заявка с сайта'
      });
      setIsSubmitting(false);
      setIsSuccess(true);
    } catch (err) {
      setIsSubmitting(false);
      setSubmitError('Не удалось отправить заявку. Попробуйте ещё раз или позвоните нам.');
    }
  }
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Форма заявки" data-id="element-15">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeForm} data-id="element-16" />

      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in" data-id="element-17">
        {/* Close button */}
        <button onClick={closeForm} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10" aria-label="Закрыть" data-id="element-18">
          <X className="w-4 h-4 text-gray-500" data-id="element-19" />
        </button>

        {isSuccess ? <div className="px-6 py-12 text-center" data-id="element-20">
            <div className="w-16 h-16 rounded-full bg-[#228B22]/10 flex items-center justify-center mx-auto mb-5" data-id="element-21">
              <CheckCircle className="w-8 h-8 text-[#228B22]" data-id="element-22" />
            </div>
            <h3 className="font-heading font-bold text-2xl text-[#0A1A3F] mb-2" data-id="element-23">
              Заявка отправлена!
            </h3>
            <p className="text-gray-500 text-base mb-6" data-id="element-24">
              Мы перезвоним вам в течение 15 минут
            </p>
            <button onClick={closeForm} className="inline-flex items-center justify-center bg-[#0A1A3F] hover:bg-[#0d2255] text-white font-heading font-bold text-base min-h-[48px] px-8 rounded-2xl transition-colors" data-id="element-25">
              Отлично
            </button>
          </div> : <>
            {/* Header */}
            <div className="bg-[#0A1A3F] px-6 py-6 text-center" data-id="element-26">
              <h3 className="font-heading font-bold text-xl text-white mb-1" data-id="element-27">
                Оставьте заявку
              </h3>
              <p className="text-white/60 text-sm" data-id="element-28">
                Перезвоним в течение 15 минут
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-6 py-6 space-y-4" data-id="element-29">
              {/* Name */}
              <div data-id="element-30">
                <label htmlFor="lead-name" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-31">
                  Ваше имя
                </label>
                <div className="relative" data-id="element-32">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-400" data-id="element-33" />
                  <input id="lead-name" type="text" value={name} onChange={handleNameChange} placeholder="Введите имя" className={`w-full pl-11 pr-4 py-3 rounded-xl border text-base transition-colors outline-none ${errors.name ? 'border-red-400 focus:border-red-500 bg-red-50/50' : 'border-gray-200 focus:border-[#228B22] bg-gray-50'}`} data-id="element-34" />
                </div>
                {errors.name && <p className="text-red-500 text-xs mt-1" data-id="element-35">{errors.name}</p>}
              </div>

              {/* Phone */}
              <div data-id="element-36">
                <label htmlFor="lead-phone" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-37">
                  Номер телефона
                </label>
                <div className="relative" data-id="element-38">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-400" data-id="element-39" />
                  <input id="lead-phone" type="tel" value={phone} onChange={handlePhoneChange} onFocus={() => {
                if (!phone) setPhone('+7');
              }} placeholder="+7 (___) ___-__-__" className={`w-full pl-11 pr-4 py-3 rounded-xl border text-base transition-colors outline-none ${errors.phone ? 'border-red-400 focus:border-red-500 bg-red-50/50' : 'border-gray-200 focus:border-[#228B22] bg-gray-50'}`} data-id="element-40" />
                </div>
                {errors.phone && <p className="text-red-500 text-xs mt-1" data-id="element-41">{errors.phone}</p>}
              </div>

              {submitError && <p className="text-red-500 text-sm text-center bg-red-50 rounded-lg py-2 px-3" data-id="element-42">
                  {submitError}
                </p>}

              {/* Submit */}
              <button type="submit" disabled={isSubmitting} className="flex items-center justify-center gap-2.5 w-full bg-[#25D366] hover:bg-[#1da851] disabled:bg-[#25D366]/60 text-white font-heading font-bold text-lg min-h-[56px] rounded-2xl transition-colors mt-2" data-id="element-43">
                {isSubmitting ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-44" /> : <>
                    <Send className="w-5 h-5" data-id="element-45" />
                    Отправить заявку
                  </>}
              </button>

              <p className="text-xs text-gray-400 text-center" data-id="element-46">
                Нажимая кнопку, вы соглашаетесь с обработкой персональных данных
              </p>
            </form>
          </>}
      </div>
    </div>;
}